import React, { forwardRef, useImperativeHandle, useRef } from "react";
import Select, {
  components,
  Props as SelectProps,
  SelectInstance,
} from "react-select";

import Label from "components/Label";
import FormFieldWrapper from "components/FormFieldWrapper/FormFieldWrapper";
import FieldError from "components/FieldError/FieldError";

export type SelectOption = {
  label: string;
  value: string | number;
};

type SingleSelectProps = {
  label?: string;
  error?: string;
  required?: boolean;
  width?: string;
  customStyles?: any;
  labelPosition?: "VERTICAL" | "HORIZONTAL";
  labelAction?: React.ReactNode;
  showValueAsSingleValue?: boolean;
  content?: string;
  underDevelopment?: boolean;
} & Omit<
  SelectProps<SelectOption, false>,
  "isMulti" | "styles" | "classNamePrefix"
>;

export interface SingleSelectHandle {
  focus: () => void;
}

const SingleSelect = forwardRef<SingleSelectHandle, SingleSelectProps>(
  (
    {
      label,
      error,
      required,
      width,
      customStyles,
      labelPosition = "VERTICAL",
      labelAction,
      showValueAsSingleValue,
      components: customComponents,
      content,
      underDevelopment,
      ...props
    },
    ref,
  ) => {
    const selectRef = useRef<SelectInstance<SelectOption, false>>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          selectRef.current?.focus();
        },
      }),
      [],
    );

    const CustomSingleValue = (singleProps: any) => {
      const { data } = singleProps;
      return (
        <components.SingleValue {...singleProps}>
          {data.value}
        </components.SingleValue>
      );
    };

    return (
      <FormFieldWrapper
        label={label ? <Label
          tooltipContent={content}
          required={required}
          underDevelopment={underDevelopment}
        >
          {label}
        </Label> : null}
        error={<FieldError error={error} />}
        labelAction={labelAction}
        containerStyle={{
          flexDirection: labelPosition === "HORIZONTAL" ? "row" : "column",
          alignItems: labelPosition === "HORIZONTAL" ? "center" : "flex-start",
          gap: labelPosition === "HORIZONTAL" ? "8px" : "",
        }}
      >
        <Select<SelectOption, false>
          {...props}
          ref={selectRef}
          isMulti={false}
          classNamePrefix="select"
          menuPosition="fixed"
          closeMenuOnSelect
          tabSelectsValue={false}
          placeholder={props.isDisabled ? "" : props.placeholder}
          styles={{
            container: (base) => ({
              ...base,
              width: width || "100%",
              flex: 1,
            }),

            control: (base, state) => ({
              ...base,
              boxShadow: "none",
              backgroundColor: state.isFocused
                ? "var(----select-menu-bg--focused)"
                : "var(--select-menu-bg--not-focused)",
              color: "var(--font-color)",
              ...customStyles,
            }),

            input: (base) => ({
              ...base,
              color: "var(--font-color)",
              fontSize: "var(--font-sm)",
            }),

            singleValue: (base) => ({
              ...base,
              color: "var(--font-color)",
            }),

            option: (base) => ({
              ...base,
              wordBreak: "break-word",
            }),
          }}
          components={{
            IndicatorSeparator: () => null,

            ...(showValueAsSingleValue
              ? {
                SingleValue: CustomSingleValue,
              }
              : {}),

            ...customComponents,
          }}
        />
      </FormFieldWrapper>
    );
  },
);

SingleSelect.displayName = "SingleSelect";

export default SingleSelect;
